import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/shared/ui/primitives';
import apiClient from '@/lib/apiClient';
import { extractPaginatedData } from '@/shared/utils/queryUtils';

interface UserOption {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface Props {
  value?: number | null;
  onChange?: (id: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  status?: 'error' | 'warning';
  initialLabel?: string;
}

async function fetchUsers(search: string) {
  const res = await apiClient.get('/admin/users', {
    params: {
      page: 0, size: 50, isDeleted: false,
      firstName: search, lastName: search, email: search, phoneNumber: search,
    },
  });
  return extractPaginatedData(res) as any;
}

export default function UserSelect({ value, onChange, placeholder, disabled, status, initialLabel }: Props) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const debounce = useRef<any>(null);

  // Only fetch when the dropdown is open (or when the user types), to avoid
  // firing a users request on every page that merely *has* the field.
  const { data, isLoading } = useQuery({
    queryKey: ['users', 'select', search],
    queryFn: () => fetchUsers(search),
    staleTime: 30_000,
    enabled: open || search.length > 0,
  });

  const list: UserOption[] = data?.content ?? [];
  const options = list.map((u) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || `User #${u.id}`;
    const contact = u.phoneNumber || u.email || '';
    return { value: u.id, label: contact ? `${name} · ${contact}` : name };
  });

  // If the controlled value isn't in current options, prepend a placeholder option so the Select can display it.
  if (value != null && !options.find((o) => o.value === value)) {
    options.unshift({ value, label: initialLabel || `User #${value}` });
  }

  const handleSearch = (v: string) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setSearch(v), 250);
  };

  return (
    <div data-allstak-mask data-sensitive>
      <Select
        value={value ?? undefined}
        onChange={(v: any) => onChange?.(v ?? null)}
        showSearch
        allowClear
        filterOption={false}
        onSearch={handleSearch}
        onOpenChange={setOpen}
        options={options}
        loading={isLoading}
        placeholder={placeholder ?? 'Search user by name, email, or phone'}
        disabled={disabled}
        status={status}
        notFoundContent={isLoading ? 'Searching…' : open ? 'No users' : 'Click to search users'}
      />
    </div>
  );
}
